import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Loader2, Trash2, Star, Youtube, MessageSquareQuote, Save, X, Upload, Image as ImageIcon } from 'lucide-react';

/**
 * AdminTestimonialsCard — Iter-51.63 light-theme refresh.
 *
 * Same full CRUD as before, but now on a light card (`bg-white`
 * + `text-slate-800`, base font `text-[13px]`) so the admin panel
 * is readable for a parent with limited eyesight. Adds a
 * "Choose File" uploader for testimonial screenshots that stores
 * to Emergent Object Storage via `/admin/testimonials/upload-image`
 * — no more copy-pasting external URLs.
 */
function coerceError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return e?.message || fallback;
}

const EMPTY_VIDEO = { type: 'video', video_id: '', video_title: '', featured: false, order: 0, active: true };
const EMPTY_TEXT  = { type: 'text',  quote: '', author: '', image_url: '', featured: false, order: 0, active: true };
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export default function AdminTestimonialsCard({ debugPin }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');
  const [draft, setDraft] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    if (!debugPin) return;
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/admin/testimonials/list', { pin: debugPin });
      setRows(data.testimonials || []);
    } catch (e) { setErr(coerceError(e, 'Failed to load testimonials.')); }
    finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [debugPin]);

  const save = async () => {
    if (!draft) return;
    setErr('');
    try {
      const { data } = await api.post('/admin/testimonials/save', { pin: debugPin, ...draft });
      setFlash(`Saved · ${data.testimonial.type} · ${data.testimonial.testimonial_id.slice(-6)}`);
      setDraft(null);
      await load();
    } catch (e) { setErr(coerceError(e, 'Failed to save.')); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete this ${t.type} testimonial? This cannot be undone.`)) return;
    setErr('');
    try {
      await api.post('/admin/testimonials/delete', { pin: debugPin, testimonial_id: t.testimonial_id });
      setFlash(`Deleted testimonial ${t.testimonial_id.slice(-6)}`);
      await load();
    } catch (e) { setErr(coerceError(e, 'Failed to delete.')); }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !draft) return;
    setErr('');
    if (!['image/jpeg','image/jpg','image/png','image/webp'].includes(file.type)) {
      setErr('Unsupported image type. Use PNG, JPG, or WEBP.');
      e.target.value = ''; return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setErr(`Image too large (${Math.round(file.size/1024)} KB). Max 5 MB.`);
      e.target.value = ''; return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('pin', debugPin);
      form.append('file', file);
      const { data } = await api.post('/admin/testimonials/upload-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Use the absolute_url so it survives being pasted anywhere; the
      // backend proxies same-origin requests too, so this is safe either way.
      setDraft(d => ({ ...d, image_url: data.absolute_url || data.url }));
      setFlash(`Uploaded ${file.name} (${Math.round(file.size/1024)} KB).`);
    } catch (ex) {
      setErr(coerceError(ex, 'Upload failed.'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const videos = rows.filter(r => r.type === 'video');
  const texts  = rows.filter(r => r.type === 'text');

  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm text-slate-800 text-[13px]"
      data-testid="admin-testimonials-card">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 font-bold text-slate-900">
          <Star size={16} className="text-amber-500"/>
          <span>Landing testimonials</span>
          <span className="text-[12px] font-medium text-slate-500">
            · {videos.length} video · {texts.length} text
          </span>
        </div>
        <div className="flex gap-1.5">
          <button data-testid="admin-testimonial-add-video" onClick={() => setDraft({ ...EMPTY_VIDEO })}
            className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-rose-600 text-white hover:brightness-110 inline-flex items-center gap-1">
            <Youtube size={13}/> Add video
          </button>
          <button data-testid="admin-testimonial-add-text" onClick={() => setDraft({ ...EMPTY_TEXT })}
            className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-sky-600 text-white hover:brightness-110 inline-flex items-center gap-1">
            <MessageSquareQuote size={13}/> Add text
          </button>
        </div>
      </div>

      {err && <div data-testid="admin-testimonials-err" className="text-[13px] text-rose-600 mb-2">{err}</div>}
      {flash && (
        <div data-testid="admin-testimonials-flash"
          className="text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-300 rounded px-2 py-1 mb-2 flex items-start justify-between gap-2">
          <span className="flex-1">{flash}</span>
          <button onClick={() => setFlash('')} className="text-emerald-700 hover:text-emerald-900 font-bold">×</button>
        </div>
      )}
      {busy && <div className="text-slate-500 text-[13px] mb-2 inline-flex items-center gap-1"><Loader2 size={13} className="animate-spin"/> Loading…</div>}

      <div className="space-y-3">
        <Group label="Videos (YouTube)" icon={<Youtube size={13}/>} colour="text-rose-700"
          rows={videos} onEdit={setDraft} onDelete={remove}/>
        <Group label="Text / screenshots" icon={<MessageSquareQuote size={13}/>} colour="text-sky-700"
          rows={texts} onEdit={setDraft} onDelete={remove}/>
      </div>

      {draft && (
        <div data-testid="admin-testimonial-editor" className="mt-3 border border-slate-300 rounded-lg p-3 bg-slate-50 space-y-2">
          <div className="text-[13px] font-bold text-amber-700 flex items-center gap-2">
            {draft.type === 'video' ? <Youtube size={14}/> : <MessageSquareQuote size={14}/>}
            {draft.testimonial_id ? 'Edit' : 'New'} {draft.type} testimonial
          </div>
          {draft.type === 'video' ? (
            <>
              <Field label="YouTube video ID (e.g. dQw4w9WgXcQ)">
                <input data-testid="admin-testimonial-video-id" value={draft.video_id || ''}
                  onChange={(e) => setDraft(d => ({ ...d, video_id: e.target.value }))}
                  placeholder="dQw4w9WgXcQ" className={inputCls + ' font-mono'}/>
              </Field>
              <Field label="Video title (optional caption)">
                <input data-testid="admin-testimonial-video-title" value={draft.video_title || ''}
                  onChange={(e) => setDraft(d => ({ ...d, video_title: e.target.value }))}
                  placeholder="Rohan reads his first book" className={inputCls}/>
              </Field>
            </>
          ) : (
            <>
              <Field label="Quote (pull text — ~120 chars)">
                <textarea data-testid="admin-testimonial-quote" value={draft.quote || ''}
                  onChange={(e) => setDraft(d => ({ ...d, quote: e.target.value }))}
                  placeholder="My son reads 3 books a week now."
                  rows={2} className={inputCls + ' resize-none'}/>
              </Field>
              <Field label="Author (e.g. Priya, Pune)">
                <input data-testid="admin-testimonial-author" value={draft.author || ''}
                  onChange={(e) => setDraft(d => ({ ...d, author: e.target.value }))}
                  placeholder="Priya, Pune" className={inputCls}/>
              </Field>
              <Field label="Screenshot — upload from device, or paste a URL">
                <div className="flex gap-2 items-start">
                  <input data-testid="admin-testimonial-image-url" value={draft.image_url || ''}
                    onChange={(e) => setDraft(d => ({ ...d, image_url: e.target.value }))}
                    placeholder="https://…  or upload with the button →"
                    className={inputCls + ' font-mono text-[12px] flex-1'}/>
                  <button type="button" data-testid="admin-testimonial-upload-btn"
                    onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="text-[12px] font-bold px-3 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-900 inline-flex items-center gap-1 disabled:opacity-60 whitespace-nowrap">
                    {uploading ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>}
                    Choose File
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp"
                    data-testid="admin-testimonial-file-input" onChange={onFileChosen} className="hidden"/>
                </div>
                {draft.image_url && (
                  <div className="mt-2 flex items-start gap-2" data-testid="admin-testimonial-image-preview">
                    <img src={draft.image_url} alt="preview"
                      className="max-h-24 rounded border border-slate-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                    <button type="button" onClick={() => setDraft(d => ({ ...d, image_url: '' }))}
                      className="text-[11px] text-rose-600 hover:underline">Remove</button>
                  </div>
                )}
                <div className="text-[12px] text-slate-500 mt-1 leading-snug">
                  Recommended: PNG or JPG, under ~2 MB. WhatsApp screenshots (portrait or
                  landscape) both display well. Max upload size is 5 MB.
                </div>
              </Field>
            </>
          )}
          <div className="grid grid-cols-3 gap-2 items-end">
            <Field label="Order (lower = first)">
              <input type="number" data-testid="admin-testimonial-order" value={draft.order ?? 0}
                onChange={(e) => setDraft(d => ({ ...d, order: parseInt(e.target.value) || 0 }))}
                className={inputCls}/>
            </Field>
            <label className="text-[12px] text-slate-700 flex items-center gap-1.5 pb-1.5">
              <input data-testid="admin-testimonial-featured" type="checkbox" checked={!!draft.featured}
                onChange={(e) => setDraft(d => ({ ...d, featured: e.target.checked }))}/>
              Featured (sorts first)
            </label>
            <label className="text-[12px] text-slate-700 flex items-center gap-1.5 pb-1.5">
              <input data-testid="admin-testimonial-active" type="checkbox" checked={!!draft.active}
                onChange={(e) => setDraft(d => ({ ...d, active: e.target.checked }))}/>
              Active (visible)
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <button data-testid="admin-testimonial-save" onClick={save}
              className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1">
              <Save size={13}/> Save
            </button>
            <button data-testid="admin-testimonial-cancel" onClick={() => setDraft(null)}
              className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-slate-200 text-slate-800 inline-flex items-center gap-1">
              <X size={13}/> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-white text-slate-800 text-[13px] rounded px-2 py-1.5 outline-none border border-slate-300 focus:border-slate-500";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12px] text-slate-500 block mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function Group({ label, icon, colour, rows, onEdit, onDelete }) {
  if (!rows.length) return (
    <div>
      <div className={`text-[12px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1 ${colour}`}>
        {icon} {label}
      </div>
      <div className="text-[12px] text-slate-500 italic">No {label.toLowerCase()} yet. Use the buttons above to add one.</div>
    </div>
  );
  return (
    <div>
      <div className={`text-[12px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1 ${colour}`}>
        {icon} {label}
      </div>
      <div className="space-y-1">
        {rows.map(t => (
          <div key={t.testimonial_id}
            data-testid={`admin-testimonial-row-${t.testimonial_id}`}
            className="border border-slate-200 rounded-lg px-2 py-2 flex items-start gap-2 bg-slate-50">
            {t.type === 'video' ? (
              <img src={`https://i.ytimg.com/vi/${t.video_id}/default.jpg`} alt="thumb"
                className="w-12 h-10 object-cover rounded flex-shrink-0"/>
            ) : t.image_url ? (
              <img src={t.image_url} alt="thumb" className="w-12 h-10 object-cover rounded flex-shrink-0"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
            ) : (
              <div className="w-12 h-10 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                <ImageIcon size={14} className="text-slate-500"/>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-slate-800 truncate">
                {t.type === 'video'
                  ? (t.video_title || t.video_id)
                  : (t.quote || t.author || 'Screenshot')}
              </div>
              <div className="text-[12px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                <span>#{t.order}</span>
                {t.featured && <span className="text-amber-600">★ featured</span>}
                {!t.active && <span className="text-rose-600">inactive</span>}
                {t.author && <span>· {t.author}</span>}
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button data-testid={`admin-testimonial-edit-${t.testimonial_id}`} onClick={() => onEdit(t)}
                className="text-[12px] font-bold px-2 py-1 rounded bg-slate-200 text-slate-800 hover:bg-slate-300">Edit</button>
              <button data-testid={`admin-testimonial-delete-${t.testimonial_id}`} onClick={() => onDelete(t)}
                className="text-[12px] font-bold px-1.5 py-1 rounded bg-rose-600 text-white hover:bg-rose-700">
                <Trash2 size={12}/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
